import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Area,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line,
  LabelList,
} from "recharts";
import {
  Loader2,
  TrendingUp,
  DollarSign,
  Target,
  Download,
  Filter,
  AlertTriangle,
  Clock,
  Award,
  Users,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { useReactToPrint } from "react-to-print";
import api from "../../api/api";
import * as S from "./styles";

const KpiCard = ({ icon: Icon, label, value, trend, colorClass }) => (
  <div className={`kpi-card ${colorClass}`}>
    <div className="kpi-icon">
      <Icon size={24} />
    </div>
    <div className="kpi-content">
      <label>{label}</label>
      <div className="kpi-value-group">
        <strong>{value}</strong>
        {trend && (
          <span className={`trend ${trend > 0 ? "up" : "down"}`}>
            {trend > 0 ? (
              <ArrowUpRight size={14} />
            ) : (
              <ArrowDownRight size={14} />
            )}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  </div>
);

const FinancialStats = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7");
  const componentRef = useRef(null);

  const [data, setData] = useState({
    products: [],
    summary: {},
    dailyTrend: [],
    orderStatus: [],
    shiftPerformance: [],
    revenueComposition: [],
  });

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `NinjaBurger_BI_Report_${new Date().toLocaleDateString()}`,
  });

  const fetchData = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        const days = parseInt(period, 10);

        const res = await Promise.allSettled([
          api.get(`/stats/top-products?days=${days}`, { signal }),
          api.get(`/stats/summary?days=${days}`, { signal }),
          api.get(`/stats/daily-trend?days=${days}`, { signal }),
          api.get(`/stats/order-status`, { signal }),
          api.get(`/stats/shift-performance`, { signal }),
          api.get(`/stats/revenue-composition?days=${days}`, { signal }),
        ]);

        const [p, s, t, st, sh, rc] = res.map((r) =>
          r.status === "fulfilled" ? r.value.data : [],
        );

        if (!signal.aborted) {
          setData({
            products: p || [],
            summary: s || {},
            dailyTrend: t || [],
            orderStatus: st || [],
            shiftPerformance: sh || [],
            revenueComposition: rc || [],
          });
        }
      } catch (err) {
        console.error("Erro BI:", err);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [period],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const stats = useMemo(() => {
    const revenue = Number(data.summary?.totalRevenue) || 0;
    const orders = Number(data.summary?.totalOrders) || 0;
    const cancelVal =
      data.orderStatus?.find((s) => s.name === "cancelado")?.value || 0;

    return {
      totalRevenue: revenue.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
      avgTicket:
        orders > 0
          ? (revenue / orders).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })
          : "R$ 0,00",
      totalOrders: orders,
      bestSeller: data.products?.[0]?.name || "N/A",
      cancelRate:
        orders > 0 ? ((cancelVal / (orders + cancelVal)) * 100).toFixed(1) : 0,
    };
  }, [data]);

  if (loading)
    return (
      <S.LoadingWrapper>
        <Loader2 className="animate-spin" /> Sincronizando Inteligência...
      </S.LoadingWrapper>
    );

  return (
    <S.Container ref={componentRef}>
      <S.ControlPanel className="no-print">
        <div className="left">
          <h1>
            <TrendingUp size={26} /> Admin Intelligence
          </h1>
        </div>
        <div className="right">
          <S.SelectGroup>
            <Filter size={16} />
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="60">Últimos 60 dias</option>
              <option value="90">Último Trimestre</option>
            </select>
          </S.SelectGroup>
          <button className="export-btn" onClick={handlePrint}>
            <Download size={16} /> PDF Report
          </button>
        </div>
      </S.ControlPanel>

      <S.KpiGrid>
        <KpiCard
          icon={DollarSign}
          label="Faturamento"
          value={stats.totalRevenue}
          trend={8.4}
          colorClass="blue"
        />
        <KpiCard
          icon={Target}
          label="Ticket Médio"
          value={stats.avgTicket}
          trend={1.2}
          colorClass="orange"
        />
        <KpiCard
          icon={Users}
          label="Pedidos"
          value={stats.totalOrders}
          trend={14}
          colorClass="purple"
        />
        <KpiCard
          icon={Award}
          label="Top Seller"
          value={stats.bestSeller}
          colorClass="emerald"
        />
      </S.KpiGrid>

      <S.MainDashboardGrid>
        {/* Gráfico de Evolução */}
        <S.ReportBox style={{ gridColumn: "span 2" }}>
          <div className="box-header">Saúde Financeira e Volume</div>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={data.dailyTrend}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f1f5f9"
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
                }}
              />
              <Area
                type="monotone"
                dataKey="total"
                fill="url(#colorTotal)"
                stroke="#2563eb"
                strokeWidth={3}
              />
              <Line
                type="monotone"
                dataKey="totalOrders"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </S.ReportBox>

        {/* NOVO CONTEÚDO: Status Operacional (Horizontal Bar) */}
        <S.ReportBox>
          <div className="box-header">Status da Operação</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              layout="vertical"
              data={data.orderStatus}
              margin={{ left: 10, right: 30 }}
            >
              <XAxis type="number" hide />
              <YAxis
                dataKey="name"
                type="category"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fontWeight: 600, fill: "#475569" }}
                width={80}
              />
              <Tooltip cursor={{ fill: "transparent" }} />
              <Bar dataKey="value" radius={[0, 10, 10, 0]} barSize={30}>
                {data.orderStatus.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.name === "concluido"
                        ? "#10b981"
                        : entry.name === "cancelado"
                          ? "#ef4444"
                          : "#3b82f6"
                    }
                  />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  style={{ fill: "#64748b", fontWeight: 700 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </S.ReportBox>

        {/* Mix de Produtos */}
        <S.ReportBox style={{ gridColumn: "span 2" }}>
          <div className="box-header">Mix de Produtos por Categoria</div>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.revenueComposition}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f1f5f9"
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11 }}
              />
              <Tooltip cursor={{ fill: "#f8fafc" }} />
              <Legend verticalAlign="top" align="right" iconType="circle" />
              <Bar
                dataKey="burgers"
                name="Burgers"
                stackId="a"
                fill="#2563eb"
              />
              <Bar
                dataKey="bebidas"
                name="Bebidas"
                stackId="a"
                fill="#10b981"
              />
              <Bar
                dataKey="acompanhamentos"
                name="Acompanhamentos"
                stackId="a"
                fill="#f59e0b"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </S.ReportBox>

        {/* Ranking Lateral */}
        <S.ReportBox>
          <div className="box-header">Ranking de Produtos (Qtd)</div>
          <div className="ranking-list">
            {data.products.slice(0, 5).map((item, idx) => (
              <div key={idx} className="ranking-item">
                <span className="rank-number">{idx + 1}</span>
                <span className="rank-name">{item.name}</span>
                <span className="rank-value">{item.vendas} un</span>
              </div>
            ))}
          </div>
        </S.ReportBox>
      </S.MainDashboardGrid>

      <S.AlertFooter className="no-print">
        <div className="alert-item warning">
          <AlertTriangle size={20} />
          <span>
            Taxa de cancelamento: <strong>{stats.cancelRate}%</strong>.
            Monitorar gargalos.
          </span>
        </div>
        <div className="alert-item success">
          <Clock size={20} />
          <span>
            Pico esperado: <strong>19h às 21h</strong>. Equipe em prontidão.
          </span>
        </div>
      </S.AlertFooter>
    </S.Container>
  );
};

export default FinancialStats;
